import { useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Upload, X, Loader2, Image as ImageIcon, Film } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

export default function FileUpload({ onUploaded, category = "general", accept = "image/*", label = "Upload File", currentUrl, className = "", resetAfterUpload = false }) {
  const { getAuthHeader } = useAuth();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentUrl || null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Local preview (only when persisted slot — skip for multi-add mode)
    if (!resetAfterUpload && file.type.startsWith("image/")) {
      setPreview(URL.createObjectURL(file));
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      // Don't set Content-Type — let the browser/axios add the multipart boundary
      const res = await axios.post(`${API}/upload?category=${category}`, formData, {
        headers: { ...getAuthHeader() },
      });
      const uploadedUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      if (resetAfterUpload) {
        // Multi-add (gallery/shorts) — don't keep a preview, let parent grid show items
        setPreview(null);
      } else {
        setPreview(uploadedUrl);
      }
      onUploaded(uploadedUrl, { ...res.data, content_type: file.type });
      toast.success("File uploaded successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
      if (!currentUrl) setPreview(null);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleRemove = () => {
    setPreview(null);
    onUploaded("", null);
  };

  const isVideo = accept.includes("video");

  return (
    <div className={className}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleFile}
        className="hidden"
        data-testid={`file-input-${category}`}
      />

      {preview ? (
        <div className="relative group">
          {isVideo && preview ? (
            <video src={preview} className="w-full h-48 object-cover rounded-lg bg-black" controls />
          ) : (
            <img src={preview} alt="Preview" className="w-full h-48 object-cover rounded-lg bg-[#1E293B]" />
          )}
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
            <button
              onClick={() => inputRef.current?.click()}
              className="px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white rounded-lg text-sm backdrop-blur-sm"
              data-testid={`change-${category}`}
            >
              Change
            </button>
            <button
              onClick={handleRemove}
              className="px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg text-sm backdrop-blur-sm"
              data-testid={`remove-${category}`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="w-full h-48 border-2 border-dashed border-white/10 hover:border-[#7C3AED]/50 rounded-lg flex flex-col items-center justify-center gap-3 transition-colors bg-white/5 hover:bg-[#7C3AED]/5"
          data-testid={`upload-btn-${category}`}
        >
          {uploading ? (
            <Loader2 className="w-8 h-8 text-[#7C3AED] animate-spin" />
          ) : isVideo ? (
            <Film className="w-8 h-8 text-[#64748B]" />
          ) : (
            <ImageIcon className="w-8 h-8 text-[#64748B]" />
          )}
          <span className="text-sm text-[#94A3B8]">{uploading ? "Uploading..." : label}</span>
          <span className="text-xs text-[#64748B]">
            {isVideo ? "MP4, WebM, MOV (max 100MB)" : "JPEG, PNG, WebP, GIF (max 10MB)"}
          </span>
        </button>
      )}
    </div>
  );
}
